import { memo, useState } from 'react'
import { Plus, Search } from 'lucide-react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

const PASTEL_COLORS = [
  '#E8B8C0', '#B8CFE8', '#B8E8B8', '#CFC0E8', '#E8D0B8',
  '#E8C0D8', '#B8E8D8', '#E8DFB8', '#C8C0E8', '#E8B8B8',
  '#D0E8B8', '#B8E8E8', '#E8D8B8', '#CFC0E8', '#E8C0D8',
  '#B8D8E8', '#E0C0E8', '#B8E8C8', '#E8D0B8', '#C8C0E8',
]

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return PASTEL_COLORS[Math.abs(hash) % PASTEL_COLORS.length]
}

interface PrivateConversation {
  participantId: string
  participantName: string
  lastMessage: string
  timestamp: number
  unreadCount?: number
}

interface PrivateChatListProps {
  conversations: PrivateConversation[]
  onSelectConversation: (participantId: string) => void
  /**
   * Omitted when the host has closed private chat. Existing threads stay
   * readable — a moderator is exempt from the gate and can still DM in — but
   * there is no way to start a new one.
   */
  onNewChat?: () => void
}

export const PrivateChatList = memo(function PrivateChatList({
  conversations,
  onSelectConversation,
  onNewChat,
}: PrivateChatListProps) {
  const t = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')

  const filteredConversations = conversations.filter((conv) =>
    conv.participantName.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))

    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60))
      return minutes < 1 ? t('panels.justNow') : `${minutes}${t('panels.minutesAgoShort')}`
    } else if (hours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="flex h-full flex-col bg-panelcolor overflow-hidden w-full">
      {conversations.length > 0 && (
        <div className="px-1 pt-2 pb-3 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('panels.searchByName')}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-muted border border-vc-panel-border rounded-lg text-sm text-slate-800 dark:text-foreground placeholder:text-slate-400 dark:placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
            />
          </div>
        </div>
      )}

      <div className={`flex-1 flex flex-col overflow-y-auto ${conversations.length === 0 ? 'justify-center items-center' : ''}`}>
        {filteredConversations.length === 0 && conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-2">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="35" cy="45" r="12" fill="#E8D5D0" />
              <path d="M20 75C20 61.19 30.19 50 44 50H26C16.59 50 8 58.59 8 68V75H20Z" fill="#E8D5D0" />
              <rect x="48" y="25" width="32" height="24" rx="4" fill="#F5C6C6" stroke="#E8A5A5" strokeWidth="1.5" />
              <rect x="52" y="31" width="20" height="2" rx="1" fill="#E8A5A5" />
              <rect x="52" y="37" width="16" height="2" rx="1" fill="#E8A5A5" />
              <circle cx="85" cy="45" r="12" fill="#E8D5D0" />
              <path d="M70 75C70 61.19 80.19 50 94 50H76C66.59 50 58 58.59 58 68V75H70Z" fill="#E8D5D0" />
              <rect x="40" y="60" width="32" height="24" rx="4" fill="#D4E8F5" stroke="#A5C8E8" strokeWidth="1.5" />
              <rect x="44" y="66" width="20" height="2" rx="1" fill="#A5C8E8" />
              <rect x="44" y="72" width="16" height="2" rx="1" fill="#A5C8E8" />
            </svg>
            <div className="text-lg font-medium text-slate-700 dark:text-foreground mb-1">
              {t('panels.noConversations')}
            </div>
            <div className="text-sm text-slate-500 dark:text-muted-foreground">
              {t('panels.clickPlusToStartChat')}
            </div>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-muted-foreground">
            {t('panels.noConversationsFound')}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredConversations.map((conv) => (
              <div
                key={conv.participantId}
                onClick={() => onSelectConversation(conv.participantId)}
                className="w-full rounded-xl flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-muted transition text-left"
              >
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center text-sm font-semibold text-slate-800 shrink-0"
                  style={{ backgroundColor: getAvatarColor(conv.participantName) }}
                >
                  {conv.participantName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-slate-800 dark:text-foreground truncate">
                      {conv.participantName}
                    </span>
                    <span className="text-xs text-slate-400 dark:text-muted-foreground ml-2 shrink-0">
                      {formatTime(conv.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500 dark:text-muted-foreground truncate">
                      {conv.lastMessage}
                    </p>
                    {/* Read conversations carry no count — not a "0". */}
                    {(conv.unreadCount ?? 0) > 0 && (
                      <p className="min-w-[20px] px-1.5 text-primary text-xs font-semibold flex items-center justify-center">
                        {(conv.unreadCount ?? 0) > 99 ? '99+' : conv.unreadCount}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {onNewChat && (
        <button
          type="button"
          onClick={onNewChat}
          className="fixed bottom-10 right-6 h-14 w-14 rounded-full bg-primary shadow-lg flex items-center justify-center text-white transition-all duration-300 hover:scale-110 active:scale-95 z-10"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}
    </div>
  )
})
